import React, {
  ChangeEvent, ClipboardEvent,
  MutableRefObject,
  useCallback, useMemo, useState,
} from 'react';
import { setChatAlert } from 'models/twilio';
import { useDispatch } from 'react-redux';
import FilePreview from '../components/FilePreview';
import { BEHAVIOR } from '../constants';
import TRANSLATIONS from '../translations';

const useTwilioSendFile = (
  fileInputRef: MutableRefObject<HTMLInputElement>,
  textAreaRef: MutableRefObject<HTMLTextAreaElement>,
) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState<File>(null);

  const addFile = useCallback((fileToSend: File) => {
    if (!fileToSend) return;
    if (fileToSend.size > BEHAVIOR.FILE_SIZE_LIMIT) {
      dispatch(setChatAlert({ message: TRANSLATIONS.COMMON.FILE_LIMIT[1], translationKey: TRANSLATIONS.COMMON.FILE_LIMIT[0], severity: 'error' }));
      return;
    }
    setFile(fileToSend);
  }, [dispatch]);

  const onFileInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile: File = e.target.files[0];
    addFile(selectedFile);
  }, [addFile]);

  const onFilePaste = useCallback((e: ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedFile: File = e.clipboardData.files?.[0];
    addFile(pastedFile);
  }, [addFile]);

  const FilePreviewComponent = useMemo(() => (
    <FilePreview
      file={file}
      setFile={setFile}
      textAreaRef={textAreaRef}
      fileInputRef={fileInputRef}
    />
  ), [file, textAreaRef, fileInputRef]);

  return useMemo(() => ({
    FilePreviewComponent,
    onFileInput,
    onFilePaste,
  }), [
    FilePreviewComponent,
    onFileInput,
    onFilePaste,
  ]);
};

export default useTwilioSendFile;
